import { cookieStorage, localStorage } from '@muse-web/storages'
import { getHttpErrorResponseMessage, isHttpError } from '@core/http-client'
import { Logger } from '@core/logger'

import {
    type TUpdateAnalyticsContextPayload,
    updateAnalyticsContext,
} from '../services/updateAnalyticsContext'
import {
    DEVICE_ID_KEY,
    EXPERIMENT_MEMBER_COOKIE_NAME,
    SESSION_ID_KEY,
} from '../constants/cookieKeys'
import { ANALYTICS_IDS_KEY } from '../constants/storageKeys'
import { type TAnalyticsContextIds } from '../types/deviceContextTypes'

import { updateMemberFirstSessionId } from './updateMemberFirstSessionId'

const CONTEXT_KEYS = ['device_id', 'session_id', 'member_experiment_id', 'user_id'] as const

const readCookieId = (name: string) => {
    const id = Number(cookieStorage.get(name))

    return Number.isFinite(id) && id > 0 ? id : undefined
}

const isSameContext = (stored: unknown, ids: TAnalyticsContextIds) =>
    typeof stored === 'object' &&
    stored !== null &&
    CONTEXT_KEYS.every(key => Reflect.get(stored, key) === ids[key])

const logSyncFailure = async (error: unknown) => {
    if (!isHttpError(error)) {
        Logger.error('Failed to update analytics context', { error })

        return
    }

    const message = await getHttpErrorResponseMessage(error)
    const details = message ? `: ${message}` : ''

    Logger.error(`Failed to update analytics context: HTTP ${error.response.status}${details}`)
}

const pushContext = async (userId: number) => {
    const deviceId = readCookieId(DEVICE_ID_KEY)

    if (deviceId === undefined) {
        return
    }

    const sessionId = readCookieId(SESSION_ID_KEY)
    const memberExperimentId = readCookieId(EXPERIMENT_MEMBER_COOKIE_NAME)
    const payload: TUpdateAnalyticsContextPayload = {
        device_id: deviceId,
        ...(sessionId !== undefined && { session_id: sessionId }),
        ...(memberExperimentId !== undefined && { member_experiment_id: memberExperimentId }),
    }
    const ids: TAnalyticsContextIds = { ...payload, user_id: userId }

    if (isSameContext(localStorage.get(ANALYTICS_IDS_KEY), ids)) {
        return
    }

    try {
        await updateAnalyticsContext(payload)
        localStorage.set(ANALYTICS_IDS_KEY, ids)
    } catch (error) {
        await logSyncFailure(error)
    }
}

/**
 * Sync the device/session identity context. The context push only runs for logged-in
 * users (guests skipped, matching audio-web) and only when the ids actually change
 * (compared against the persisted snapshot). `updateMemberFirstSessionId` runs for
 * everyone — member-experiment cohorts are largely anonymous, so guests' first-session
 * id must be recorded too. The user id is passed in by the app-layer container that reads
 * the store — core must not import features.
 */
export const syncAnalyticsContext = async (userId: number) => {
    if (userId) {
        await pushContext(userId)
    }

    updateMemberFirstSessionId()
}
